/**
 * CalorieTrackerPanel.jsx — Rê Leve IA (Etapa 5)
 * Painel de acompanhamento calórico informativo.
 * Modo cliente: simples, sem dados técnicos.
 * Modo desenvolvedor: estado técnico completo, logs, botão limpar.
 */

import { useState, useEffect } from 'react';
import {
  getConsentStatus,
  getConsentStatusLabel,
  CONSENT_STATUS,
  grantCalorieConsent,
} from '../lib/consentEngine';
import {
  getCaloriePlan,
  getDailyCalorieSummary,
  getWeeklyCalorieSummary,
  suggestNextMeal,
  DISCLAIMER,
} from '../lib/calorieTrackerEngine';
import {
  getCustomerLocalState,
  clearAllClientData,
} from '../lib/localStorageEngine';
import { getDeclaredRestrictions } from '../lib/restrictionEngine';

// ── Modo Cliente ───────────────────────────────────────────────────────────────

function ClientCalorieView({ clienteId, onConsentActivate }) {
  const [status, setStatus] = useState(() => getConsentStatus(clienteId));
  const [daily, setDaily] = useState(null);
  const [suggestion, setSuggestion] = useState(null);

  useEffect(() => {
    const s = getConsentStatus(clienteId);
    setStatus(s);
    if (s === CONSENT_STATUS.GRANTED) {
      setDaily(getDailyCalorieSummary(clienteId));
      setSuggestion(suggestNextMeal(clienteId));
    }
  }, [clienteId]);

  function handleActivate() {
    grantCalorieConsent(clienteId);
    setStatus(CONSENT_STATUS.GRANTED);
    setDaily(getDailyCalorieSummary(clienteId));
    setSuggestion(suggestNextMeal(clienteId));
    if (onConsentActivate) onConsentActivate();
  }

  if (!clienteId) {
    return (
      <div className="calorie-card calorie-card--inactive">
        <span className="calorie-icon">📊</span>
        <p className="calorie-title">Acompanhamento calórico informativo</p>
        <p className="calorie-desc">Registre suas refeições Rê Leve e acompanhe sua rotina alimentar de forma prática.</p>
        <p className="calorie-disclaimer">⚠️ {DISCLAIMER}</p>
      </div>
    );
  }

  if (status !== CONSENT_STATUS.GRANTED) {
    return (
      <div className="calorie-card calorie-card--inactive">
        <span className="calorie-icon">📊</span>
        <p className="calorie-title">Acompanhamento calórico informativo</p>
        <p className="calorie-desc">
          Registre suas refeições Rê Leve e acompanhe sua rotina alimentar de forma prática.
        </p>
        <p className="calorie-disclaimer">⚠️ {DISCLAIMER}</p>
        {status === CONSENT_STATUS.DENIED ? (
          <p className="calorie-denied-note">Você optou por não ativar o acompanhamento. Pode reativar a qualquer momento.</p>
        ) : null}
        <button className="cs-btn cs-btn--calorico" onClick={handleActivate}>
          Ativar acompanhamento informativo
        </button>
      </div>
    );
  }

  return (
    <div className="calorie-card calorie-card--active">
      <div className="calorie-header">
        <span className="calorie-icon">📊</span>
        <span className="calorie-status-badge calorie-status--granted">Autorizado</span>
      </div>
      <p className="calorie-title">Acompanhamento calórico informativo</p>

      {daily && (
        <div className="calorie-summary">
          <div className="calorie-row">
            <span>Refeições registradas hoje</span>
            <span className="calorie-value">{daily.mealCount}</span>
          </div>
          <div className="calorie-row">
            <span>Estimativa do dia</span>
            <span className="calorie-value">{daily.totalKcal} kcal</span>
          </div>
          {daily.meals.length > 0 && (
            <div className="calorie-meals-list">
              {daily.meals.map((m, i) => (
                <div key={i} className="calorie-meal-item">
                  <span>{m.itemName}</span>
                  <span className="calorie-meal-kcal">~{m.estimatedKcal} kcal</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {suggestion && !suggestion.suggestion?.includes('Todas') && (
        <div className="calorie-suggestion">
          <p>{suggestion.suggestion}</p>
        </div>
      )}

      <p className="calorie-disclaimer">⚠️ {DISCLAIMER}</p>
    </div>
  );
}

// ── Modo Desenvolvedor ─────────────────────────────────────────────────────────

function DevCalorieView({ clienteId }) {
  const [localState, setLocalState] = useState(null);
  const [cleared, setCleared] = useState(false);

  useEffect(() => {
    setCleared(false);
    if (clienteId) {
      setLocalState(getCustomerLocalState(clienteId));
    } else {
      setLocalState(null);
    }
  }, [clienteId]);

  function handleClear() {
    if (!clienteId) return;
    clearAllClientData(clienteId);
    setLocalState(getCustomerLocalState(clienteId));
    setCleared(true);
  }

  function refresh() {
    if (clienteId) setLocalState(getCustomerLocalState(clienteId));
  }

  if (!clienteId) {
    return (
      <div className="card">
        <h3 className="card-title">Acompanhamento Calórico</h3>
        <p className="card-placeholder-text">Selecione um cliente para ver o acompanhamento.</p>
      </div>
    );
  }

  const consentLabel = getConsentStatusLabel(clienteId);
  const restrictions = getDeclaredRestrictions(clienteId);
  const plan = getCaloriePlan(clienteId);
  const daily = getDailyCalorieSummary(clienteId);

  return (
    <div className="card">
      <h3 className="card-title">Acompanhamento Calórico — Desenvolvedor</h3>

      <div className="dev-calorie-notice">
        Esses dados estão apenas no navegador para fins de protótipo.
      </div>

      {cleared && (
        <div className="dev-calorie-cleared">Dados locais de {clienteId} foram limpos.</div>
      )}

      <div className="dev-section">
        <p className="dev-section-label">Consentimento</p>
        <div className={`calorie-status-badge calorie-status--${localState?.consent?.status || 'not_requested'}`}>
          {consentLabel}
        </div>
        {localState?.consent && (
          <div className="dev-info-row">
            <span className="dev-label">Mensagem:</span>
            <span className="dev-value">{localState.consent.message}</span>
          </div>
        )}
        {localState?.consent?.timestamp && (
          <div className="dev-info-row">
            <span className="dev-label">Quando:</span>
            <span className="dev-value dev-value--code">{localState.consent.timestamp}</span>
          </div>
        )}
      </div>

      <div className="dev-section">
        <p className="dev-section-label">Restrições declaradas ({restrictions.length})</p>
        {restrictions.length === 0
          ? <p className="dev-empty">Nenhuma restrição declarada.</p>
          : restrictions.map((r, i) => (
              <div key={i} className="dev-info-row">
                <span className="dev-label">{r.label}</span>
                <span className="dev-value dev-value--code">{r.type}</span>
                {r.isGraveAlert && <span className="dev-badge dev-badge--alerta">Alergia</span>}
              </div>
            ))
        }
      </div>

      {plan && !plan.isCollective && (
        <div className="dev-section">
          <p className="dev-section-label">Plano calórico gerado</p>
          <div className="dev-info-row"><span className="dev-label">Kit:</span><span className="dev-value">{plan.kitName}</span></div>
          <div className="dev-info-row"><span className="dev-label">Total estimado:</span><span className="dev-value">{plan.totalEstimatedKcal} kcal</span></div>
          {plan.isWeekly && <div className="dev-info-row"><span className="dev-label">Semanal:</span><span className="dev-value">{plan.weeklyNote}</span></div>}
        </div>
      )}

      <div className="dev-section">
        <p className="dev-section-label">Logs calóricos de hoje ({daily?.mealCount || 0})</p>
        {(daily?.meals || []).length === 0
          ? <p className="dev-empty">Nenhuma refeição registrada hoje.</p>
          : daily.meals.map((m, i) => (
              <div key={i} className="dev-info-row">
                <span className="dev-label">{m.itemName}</span>
                <span className="dev-value">~{m.estimatedKcal} kcal</span>
              </div>
            ))
        }
      </div>

      <div className="dev-section">
        <p className="dev-section-label">Eventos da sessão ({(localState?.sessionEvents || []).length})</p>
        {(localState?.sessionEvents || []).length === 0
          ? <p className="dev-empty">Sem eventos registrados.</p>
          : (localState?.sessionEvents || []).slice(-5).map((e, i) => (
              <div key={i} className="dev-info-row">
                <span className="dev-label">{e.eventType}</span>
                <span className="dev-value">{e.description || e.intent || ''}</span>
              </div>
            ))
        }
      </div>

      <div className="dev-calorie-actions">
        <button className="btn-secondary btn--small" onClick={refresh}>Atualizar</button>
        <button className="dev-clear-btn" onClick={handleClear}>
          Limpar dados locais de {clienteId}
        </button>
      </div>
    </div>
  );
}

// ── Componente principal ───────────────────────────────────────────────────────

export default function CalorieTrackerPanel({ customer, mode = 'client', onConsentActivate }) {
  const clienteId = customer?.clienteId || customer?.id || null;

  if (mode === 'developer') {
    return <DevCalorieView clienteId={clienteId} />;
  }

  return <ClientCalorieView clienteId={clienteId} onConsentActivate={onConsentActivate} />;
}
